import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import type {
  LibraryParameterDefinitionTableColumnValueQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {libraryDefinitionDataTypeToDataParameterFieldType} from 'procedures/parameter-library/libraryParameterDefinitionDataTypeConversion';
import {isLibraryParameterDefinitionDataType} from 'procedures/parameter-library/ParameterLibraryTypes';
import {convertDataParameterFieldTypeToTypedValue} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import {LibraryParameterDefinitionConfigTableResolvedColumn} from 'procedures/tables/ProcedureTableConstants';
import {assertNever} from 'util/Types';

import type {
  GetLibraryParameterDefinitionForTableColumnQuery,
  GetLibraryParameterDefinitionForTableColumnQueryVariables,
} from './__generated/GetLibraryParameterDefinitionForTableColumnQuery';

const GET_LIBRARY_PARAMETER_DEFINITION_FOR_TABLE_COLUMN = gql`
  query GetLibraryParameterDefinitionForTableColumnQuery($id: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    libraryParameterDefinition(id: $id) {
      id
      description
      dataType
    }
  }
`;

export async function resolveLibraryParameterDefinitionTableColumnValueFromId(
  query: LibraryParameterDefinitionTableColumnValueQuery
): Promise<ServerResolutionResult> {
  const {libraryParameterDefinitionId, column} = query;

  const result = await ApolloWrappers.doQueryWithoutCache<
    GetLibraryParameterDefinitionForTableColumnQuery,
    GetLibraryParameterDefinitionForTableColumnQueryVariables
  >(
    {query: GET_LIBRARY_PARAMETER_DEFINITION_FOR_TABLE_COLUMN},
    {variables: {id: libraryParameterDefinitionId}}
  );
  const definition = result.libraryParameterDefinition;

  switch (column) {
    case LibraryParameterDefinitionConfigTableResolvedColumn.DESCRIPTION:
      return makeCustomResolutionResultFromTypedValue({
        type: BaseTypedValueType.STRING,
        rawValue: definition.description ?? '',
      });
    case LibraryParameterDefinitionConfigTableResolvedColumn.TYPE:
      assert(
        isLibraryParameterDefinitionDataType(definition.dataType),
        `Unexpected library parameter data type: ${definition.dataType}`
      );
      return makeCustomResolutionResultFromTypedValue(
        convertDataParameterFieldTypeToTypedValue(
          libraryDefinitionDataTypeToDataParameterFieldType(definition.dataType)
        )
      );
    default:
      assert(false, `Unexpected library parameter definition table column: ${assertNever(column)}`);
  }
}
